import { useEffect, useState } from 'react';
import { useParams, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../../store/user/user.selector';
import { areFriends } from '../../../../utils/firebase/friends';
import { fetchPublicProfile } from '../../../../utils/firebase/publicProfile';
import { useSystemInitialization } from '../../../../hooks/useSystemInitialization';
import LoadingIcon from '../../../../components/loading-icon/LoadingIcon';
import {
  FriendCollectionPageContainer,
  FriendCollectionHeader,
  BackButton
} from './FriendCollectionPage.styles.js';

export default function FriendCollectionPage() {
  const { friendUID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector(selectUser);
  const { checklistSportDefault, checklistYearDefault, checklistManufacturerDefault } = useSystemInitialization();
  const [friendUser, setFriendUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Auto-redirect to default sport/year/manufacturer if at base collection route
  useEffect(() => {
    // Only redirect if we're at the exact base route (no sport/year/manufacturer in path)
    if (friendUser && location.pathname === `/friends/${friendUID}/collection`) {
      navigate(`/friends/${friendUID}/collection/${checklistSportDefault}/${checklistYearDefault}/${checklistManufacturerDefault}`, { replace: true });
    }
  }, [friendUser, friendUID, location.pathname, navigate, checklistSportDefault, checklistYearDefault, checklistManufacturerDefault]);

  useEffect(() => {
    if (!currentUser || !friendUID) {
      return;
    }

    const checkFriendshipAndLoadUser = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const areFriendsResult = await areFriends(currentUser.uid, friendUID);

        if (!areFriendsResult) {
          setError('You must be friends with this user to view their collection.');
          setIsLoading(false);
          return;
        }

        const friendData = await fetchPublicProfile(friendUID);
        if (!friendData) {
          setError('User not found.');
          setIsLoading(false);
          return;
        }

        setFriendUser({
          uid: friendUID,
          ...friendData
        });
      } catch (err) {
        console.error('Error loading friend collection:', err);
        setError('Error loading friend collection.');
      } finally {
        setIsLoading(false);
      }
    };

    checkFriendshipAndLoadUser();
  }, [currentUser, friendUID]);

  if (isLoading) {
    return (
      <FriendCollectionPageContainer>
        <LoadingIcon />
      </FriendCollectionPageContainer>
    );
  }

  if (error) {
    return (
      <FriendCollectionPageContainer>
        <FriendCollectionHeader>
          <BackButton onClick={() => navigate('/friends')}>← Back to Friends</BackButton>
          <div style={{ color: '#FFFFFF', padding: '24px' }}>
            <h2>Error</h2>
            <p>{error}</p>
          </div>
        </FriendCollectionHeader>
      </FriendCollectionPageContainer>
    );
  }

  if (!friendUser) {
    return (
      <FriendCollectionPageContainer>
        <FriendCollectionHeader>
          <BackButton onClick={() => navigate('/friends')}>← Back to Friends</BackButton>
          <div style={{ color: '#FFFFFF', padding: '24px' }}>
            <p>User not found.</p>
          </div>
        </FriendCollectionHeader>
      </FriendCollectionPageContainer>
    );
  }

  return (
    <FriendCollectionPageContainer>
      <FriendCollectionHeader>
        <BackButton onClick={() => navigate('/friends')}>← Back to Friends</BackButton>
        <div style={{ color: '#FFFFFF', padding: '12px 0' }}>
          <h2>{friendUser.username ? `${friendUser.username}'s Collection` : 'Collection'}</h2>
          {friendUser.collectionCardCount !== undefined && (
            <p style={{ opacity: 0.7, fontSize: '14px' }}>
              {friendUser.collectionCardCount} cards
            </p>
          )}
        </div>
      </FriendCollectionHeader>
      <Outlet context={{ viewingUserID: friendUID }} />
    </FriendCollectionPageContainer>
  );
}

